import dayjs, { Dayjs } from 'dayjs';

export const __test__ = {
  findHighPercentilePoints,
  findFrequentTimes,
  createSetPerTimeRange,
  filterByRank,
  calculateRankValues,
  convertRankArrays,
  getCompositeRanks,
  concatContinuedTimes
};

// Export types for testing
export type TestHighPercentilePoints = HighPercentilePoints;
export type TestFrequentPointWithParam = FrequentPointWithParam;
export type TestFrequentPoints = FrequentPoints;
export type TestFrequentPointsSet = FrequentPointsSet;
export type TestRankValue = RankValue;
export type TestRankValues = RankValues;

interface HighPercentilePoint {
  timestamp: Dayjs,
  percentileRanks: number[]
}

interface FrequentPointWithParam {
  timestamp: Dayjs,
  params: HighPercentilePoint[],
}

interface RankValue {
  timestamp: Dayjs,
  dayCount: number,
  meanPercentileSize: number,
  meanPercentileRank: number
}

type HighPercentilePoints = HighPercentilePoint[];

type FrequentPoints = FrequentPointWithParam[];

type FrequentPointsSet = FrequentPoints[];

type RankValues = RankValue[];

export interface Activity {
  timestamp: Dayjs,
  isActive: boolean,
  isPositionChanged: boolean
}

const PERCENTILE_THRESHOLD: number = 60;

export function findHighRiskTimeRange(valuesList: number[][], timestamps: Dayjs[], activities: Activity[]): Dayjs[][] {

  const _highPercentilePoints = findHighPercentilePoints(valuesList, timestamps);

  const _stableDates = filterStableDates(activities, _highPercentilePoints.map((point) => point.timestamp));

  const _stablePoints = _highPercentilePoints.filter((point) => _stableDates.includes(point.timestamp));

  const _frequentPoints = findFrequentTimes(_stablePoints);

  const _frequentPointsSet = createSetPerTimeRange(_frequentPoints);

  const _highRankPointsSet = filterByRank(_frequentPointsSet);

  return concatContinuedTimes(_highRankPointsSet);
}

export function findHighRiskPointIndexes(mainValues: number[], subValuesList: number[][]
): number[] {
  const _mainOverIndexes: number[] = getOverPercentileIndexes(mainValues, PERCENTILE_THRESHOLD);

  const OverPercentileIndexes: number[] = [];
  for (const _subValues of subValuesList) {
    const _subOverIndexes = getOverPercentileIndexes(_subValues, PERCENTILE_THRESHOLD);

    for (const _mainIndex of _mainOverIndexes) {
      if (_subOverIndexes.includes(_mainIndex) && !OverPercentileIndexes.includes(_mainIndex)) {
        OverPercentileIndexes.push(_mainIndex);
      }
    }
  }

  return OverPercentileIndexes.sort((a, b) => a - b);
}

// helper functions for findHighRiskTimeRange
function findHighPercentilePoints(valuesList: number[][], timestamps: Dayjs[]): HighPercentilePoints {
  if (!valuesList.length) return [];

  const _overPercentileIndexesList: number[][] = [];
  const _sortedValuesList: number[][] = [];
  for (const _values of valuesList) {
    const _overIndexes = getOverPercentileIndexes(_values, PERCENTILE_THRESHOLD);
    const _sortedValues = [..._values].sort((a, b) => b - a);

    _overPercentileIndexesList.push(_overIndexes);
    _sortedValuesList.push(_sortedValues);
  }

  const _uniqueIndexes = new Set<number>(_overPercentileIndexesList.flat());
  const highPercentilePoints: HighPercentilePoints = [];
  for (const _uniqueIndex of Array.from(_uniqueIndexes)) {
    const _percentileRanks: number[] = [];

    for (let i = 0; i < _overPercentileIndexesList.length; i++) {
      if (_overPercentileIndexesList[i].includes(_uniqueIndex)) {
        const _value = valuesList[i][_uniqueIndex];
        const _valueLength = valuesList[i].length;
        const _percentileRank = _valueLength - _sortedValuesList[i].indexOf(_value) ;

        _percentileRanks.push(_percentileRank);
      }
    }

    if (_percentileRanks.length >= 2) {
      highPercentilePoints.push({
        timestamp: timestamps[_uniqueIndex],
        percentileRanks: _percentileRanks
      });
    }
  }

  return highPercentilePoints;
}

function filterStableDates(activities: Activity[], highRiskPoints: Dayjs[]): Dayjs[] {
  const activityCountThreshold: number = 4;
  const consecutiveTimes: Dayjs[] = [];

  for (const targetDt of highRiskPoints) {
    const twoHoursBefore = targetDt.subtract(2, 'hour');
    const oneMinuteLater = targetDt.add(1, 'minute');

    // 対象の時間範囲内のアクティビティをフィルタリング
    const targetActivities = activities.filter((activity) => {
      const timestamp = activity.timestamp;
      return timestamp.isAfter(twoHoursBefore) && timestamp.isBefore(oneMinuteLater);
    });

    // 条件チェック
    if (targetActivities.length >= activityCountThreshold) {
      // 姿勢とアクティビティステータスがすべて同じかチェック

      const activeCounts = targetActivities.filter((a) => a.isActive).length;
      const positionChangeCounts = targetActivities.filter((a) => a.isPositionChanged).length;

      if (activeCounts === 0 && positionChangeCounts === 0) {
        consecutiveTimes.push(targetDt);
      }
    }
  }

  return consecutiveTimes;
}

function findFrequentTimes(highPercentilePoints: HighPercentilePoints): FrequentPoints {
  const frequentCountThreshold: number = 5;
  const _baseDate: Dayjs = dayjs("2021-01-01");

  if (!highPercentilePoints.length) return [];

  const _times = highPercentilePoints.map((_time) => _time.timestamp.format('HH:mm:ss'));
  const timeCounts = new Map<string, number>();

  _times.forEach((time) => {
    timeCounts.set(time, (timeCounts.get(time) || 0) + 1);
  });

  const frequentTimes = Array.from(timeCounts.entries())
      .filter(([_, count]) => count >= frequentCountThreshold)

  const frequentPoints: FrequentPoints = [];

  for (const [time, _] of frequentTimes) {
    const indexes: number[] = [];
    _times.forEach((element, index) => {
      if (element === time) {
        indexes.push(index);
      }
    })
    frequentPoints.push({
      timestamp: dayjs(`${_baseDate.format('YYYY-MM-DD')} ${time}`),
      params: highPercentilePoints.filter((_time, index) => indexes.includes(index))
    })
  }

  return frequentPoints.sort((a, b) => a.timestamp.diff(b.timestamp));

}

function createSetPerTimeRange(frequentPoints: FrequentPoints): FrequentPointsSet {

  if (!frequentPoints.length) return [];

  const setMinRange: number = 30;
  const frequentPointsSet: FrequentPointsSet = [];

  let _aSetOfFrequentPoints: FrequentPoints = [frequentPoints[0]];
  for (let i = 1; i < frequentPoints.length; i++) {
    const _frequentPoint = frequentPoints[i];
    const _lastFrequentPoint = frequentPoints[i-1];

    if (_frequentPoint.timestamp.diff(_lastFrequentPoint.timestamp, 'minute') > setMinRange) {
      frequentPointsSet.push(_aSetOfFrequentPoints);
      _aSetOfFrequentPoints = [_frequentPoint];
      continue;
    }

    _aSetOfFrequentPoints.push(_frequentPoint);
  }

  // add the last set
  frequentPointsSet.push(_aSetOfFrequentPoints);

  return frequentPointsSet;
}

function filterByRank(frequentPointsSet: FrequentPointsSet): FrequentPointsSet {
  const rankThreshold: number = 3;

  const _topRankPoints: FrequentPoints = frequentPointsSet.map(
    (_frequentPoints) => {
      if (_frequentPoints.length === 1) return _frequentPoints[0];

      const _compositeRanks: number[] = getFrequentPointsRanks(_frequentPoints);

      return _frequentPoints[_compositeRanks.indexOf(Math.min(..._compositeRanks))];
    }
  )

  const _ranks: number[] = getFrequentPointsRanks(_topRankPoints);

  const filteredFrequentPointsSet: FrequentPointsSet = [];
  for (let _targetRank = 1; _targetRank <= rankThreshold; _targetRank++) {

    for (let i = 0; i < frequentPointsSet.length; i++) {
      const _rank = _ranks[i];

      if (_rank === _targetRank) {
        filteredFrequentPointsSet.push(frequentPointsSet[i]);
        if (filteredFrequentPointsSet.length === rankThreshold) return filteredFrequentPointsSet;
      }
    }
  }

  return filteredFrequentPointsSet;

}

function getCompositeRanks(...valueArrays: number[][]): number[] {
  if (!valueArrays.length) return [];

  const getRanks = (values: number[]): number[] => {
    const indexed = values.map((value, index) => ({ value, index }));
    indexed.sort((a, b) => b.value - a.value);

    const ranks: number[] = new Array(values.length);
    let currentRank = 1;

    for (let i = 0; i < indexed.length; i++) {
      if (i > 0 && indexed[i].value !== indexed[i - 1].value) {
        currentRank++;
      }
      ranks[indexed[i].index] = currentRank;
    }

    return ranks;
  }

  const arrayEqual = (arr1: number[], arr2: number[]): boolean => {
    return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
  }

  const rankArrays: number[][] = valueArrays.map((arr) => getRanks(arr));
  const length = rankArrays[0].length;

  const indexed = Array.from({ length }, (_, i) => ({
    index: i,
    ranks: rankArrays.map((arr) => arr[i])
  }));

  indexed.sort((a, b) => {
    for (let i = 0; i < rankArrays.length; i++) {
      if (a.ranks[i] !== b.ranks[i]) {
        return a.ranks[i] - b.ranks[i];
      }
    }
    return 0;
  })

  const finalRanks: number[] = new Array(length);
  let currentRank = 1;

  for (let i = 0; i < indexed.length; i++) {
    if (i > 0 && !arrayEqual(indexed[i].ranks, indexed[i - 1].ranks)) {
      currentRank++;
    }
    finalRanks[indexed[i].index] = currentRank;
  }

  return finalRanks;
}

function concatContinuedTimes(frequentPointsSet: FrequentPointsSet): Dayjs[][] {

  const continuedLengthThreshold: number = 3;
  let continuedTimes: Dayjs[][] = [];
  for (const _frequentPoints of frequentPointsSet) {
    const _frequentPointsLength = _frequentPoints.length;

    if (_frequentPointsLength <= continuedLengthThreshold) {

      if (_frequentPointsLength === 1) {
        continuedTimes.push([_frequentPoints[0].timestamp]);
        continue;
      }

      continuedTimes.push([_frequentPoints[0].timestamp, _frequentPoints[_frequentPointsLength - 1].timestamp]);
      continue;
    }

    const _ranks = getFrequentPointsRanks(_frequentPoints);
    const _highestRankPoint = _frequentPoints[_ranks.indexOf(1)];
    continuedTimes.push([_highestRankPoint.timestamp]);
  }

  return continuedTimes.sort((a, b) => a[0].diff(b[0]));
}

// helper functions for findHighRiskPointIndexes
function getOverPercentileIndexes(values: number[], percentile: number):  number[] {
  const _percentileThreshold = calcPercentile(values, percentile);

  const indexes: number[] = [];

  values.forEach((value, index) => {
    if (value > _percentileThreshold) {
      indexes.push(index);
    }
  });

  return indexes;
}

function calcPercentile(values: number[], percentile: number): number {
  if (values.length === 0) return 0;

  const sortedValues = [...values].sort((a, b) => a - b);

  const percentileIndex = (percentile / 100) * (sortedValues.length - 1);

  if (Math.floor(percentileIndex) === percentileIndex) {
    return sortedValues[percentileIndex];
  }

  const lowerIndex = Math.floor(percentileIndex);
  const upperIndex = Math.ceil(percentileIndex);
  const weight = percentileIndex - lowerIndex;

  return sortedValues[lowerIndex] * (1 - weight) + sortedValues[upperIndex] * weight;
}

// rank value functions
function calculateRankValues(frequentPoint: FrequentPointWithParam): RankValue {

  const timestamp = frequentPoint.timestamp;

  const dayCount = frequentPoint.params.length;

  const meanPercentileSize = frequentPoint.params.map(
      (point) => point.percentileRanks.length)
      .reduce((a, b) => a + b) / dayCount;

  const meanPercentileRank = frequentPoint.params.map(
      (point) => point.percentileRanks.reduce((a, b) => a + b) / point.percentileRanks.length)
      .reduce((a, b) => a + b) / dayCount;

  return {timestamp, dayCount, meanPercentileSize, meanPercentileRank};
}

function convertRankArrays(rankValues: RankValues): number[][] {
  if (!rankValues.length) return [];

  const rankArrays: number[][] = [];
  const _numericKeys = Object.keys(rankValues[0]).filter((key) => key !== 'timestamp');
  for (const _key of _numericKeys) {
    const _keyValues: number[] = rankValues.map((_rankValue) => _rankValue[_key as keyof RankValue] as number);
    rankArrays.push(_keyValues);
  }

  return rankArrays;
}

function getFrequentPointsRanks(frequentPoints: FrequentPoints): number[] {
  const _rankValues: RankValues = frequentPoints.map((_frequentPoint) => calculateRankValues(_frequentPoint));
  const _rankValueArrays: number[][] = convertRankArrays(_rankValues);

  return getCompositeRanks(..._rankValueArrays);
}
